<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import {
  EMPTY_VALUE,
  buildAnswerRows,
  contactLabel,
  formatDateTime,
  formatScore,
} from '../helper/surveyResponseHelper';
import {
  isKycRequestProjection,
  operationalProjectionDestination,
  operationalProjectionKycSummaryRows,
  operationalProjectionStatusLabel,
  operationalProjectionTargetLabel,
  operationalProjectionTone,
} from '../helper/operationalProjectionHelper';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * Full detail of one collected response: delivery metadata plus every answer
 * rendered against its question (labels, not raw keys).
 */
const props = defineProps({
  show: { type: Boolean, default: false },
  response: { type: Object, default: null },
  questions: { type: Array, default: () => [] },
});

const emit = defineEmits(['close']);

const { t, statusLabel, statusTone, questionTypeLabel } = useSurveyLabels();
const route = useRoute();
const router = useRouter();

const operationalProjection = computed(
  () => props.response?.operational_projection || null
);

const isKycRequest = computed(() =>
  isKycRequestProjection(operationalProjection.value)
);

const answerRows = computed(() =>
  props.response && !isKycRequest.value
    ? buildAnswerRows(props.questions, props.response.answers, {
        yes: t('SURVEYS.PREVIEW.YES'),
        no: t('SURVEYS.PREVIEW.NO'),
      })
    : []
);

// Rows without a value are dropped instead of rendering a column of dashes:
// a response that was never opened has no `opened_at`, and the API omits
// `expires_at` when the link does not expire.
const metaRows = computed(() => {
  if (!props.response || isKycRequest.value) return [];
  const response = props.response;
  return [
    {
      label: t('SURVEYS.RESPONSES.DETAIL.CONTACT'),
      value: contactLabel(response, t('SURVEYS.RESPONSES.ANONYMOUS')),
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.SCORE'),
      value: formatScore(response.score),
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.CHANNEL'),
      value: response.delivery_channel || EMPTY_VALUE,
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.SENT_AT'),
      value: formatDateTime(response.sent_at),
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.OPENED_AT'),
      value: formatDateTime(response.opened_at),
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.RESPONDED_AT'),
      value: formatDateTime(response.responded_at),
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.EXPIRES_AT'),
      value: formatDateTime(response.expires_at),
    },
  ].filter(row => row.value !== EMPTY_VALUE);
});

const operationalProjectionStatusMessages = computed(() => ({
  created: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.DESCRIPTION.CREATED'
  ),
  reused: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.DESCRIPTION.REUSED'
  ),
  skipped: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.DESCRIPTION.SKIPPED'
  ),
  failed: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.DESCRIPTION.FAILED'
  ),
}));

const operationalFlowLabels = computed(() => ({
  generic: t('SURVEYS.OPERATIONAL_FLOWS.GENERIC'),
  visit_request: t('SURVEYS.OPERATIONAL_FLOWS.VISIT_REQUEST'),
  property_offer: t('SURVEYS.OPERATIONAL_FLOWS.PROPERTY_OFFER'),
  kyc_request: t('SURVEYS.OPERATIONAL_FLOWS.KYC_REQUEST'),
  listing_intake: t('SURVEYS.OPERATIONAL_FLOWS.LISTING_INTAKE'),
  buyer_qualification: t('SURVEYS.OPERATIONAL_FLOWS.BUYER_QUALIFICATION'),
  tenant_qualification: t('SURVEYS.OPERATIONAL_FLOWS.TENANT_QUALIFICATION'),
  owner_onboarding: t('SURVEYS.OPERATIONAL_FLOWS.OWNER_ONBOARDING'),
  inspection: t('SURVEYS.OPERATIONAL_FLOWS.INSPECTION'),
  contract_signature: t('SURVEYS.OPERATIONAL_FLOWS.CONTRACT_SIGNATURE'),
  maintenance_request: t('SURVEYS.OPERATIONAL_FLOWS.MAINTENANCE_REQUEST'),
  post_sale_follow_up: t('SURVEYS.OPERATIONAL_FLOWS.POST_SALE_FOLLOW_UP'),
}));

const operationalProjectionStatus = computed(
  () => operationalProjection.value?.status || 'failed'
);

const operationalProjectionTarget = computed(() =>
  operationalProjectionTargetLabel(operationalProjection.value, t, {
    includeId: !isKycRequest.value,
  })
);

const operationalProjectionTitle = computed(() =>
  isKycRequest.value
    ? t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.TITLE')
    : t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.TITLE')
);

const inspectionKindLabels = computed(() => ({
  entrada: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.INSPECTION_KINDS.ENTRADA'
  ),
  saida: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.INSPECTION_KINDS.SAIDA'
  ),
}));

const inspectionStatusLabels = computed(() => ({
  registrada: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.INSPECTION_STATUSES.REGISTRADA'
  ),
  aceita: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.INSPECTION_STATUSES.ACEITA'
  ),
  contestada: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.INSPECTION_STATUSES.CONTESTADA'
  ),
}));

const acceptanceStatusLabels = computed(() => ({
  pending: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.ACCEPTANCE_STATUSES.PENDING'
  ),
  accepted: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.ACCEPTANCE_STATUSES.ACCEPTED'
  ),
  contested: t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.ACCEPTANCE_STATUSES.CONTESTED'
  ),
}));

const inspectionKindLabel = value =>
  inspectionKindLabels.value[value] || EMPTY_VALUE;

const inspectionStatusLabel = value =>
  inspectionStatusLabels.value[value] || EMPTY_VALUE;

const acceptanceStatusLabel = value =>
  acceptanceStatusLabels.value[value] || EMPTY_VALUE;

const inspectionProjectionRows = projection => {
  if (projection.target_type !== 'LeaseInspection') return [];

  return [
    {
      label: t(
        'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.INSPECTION_TYPE'
      ),
      value: inspectionKindLabel(projection.inspection_kind),
    },
    {
      label: t(
        'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.INSPECTION_STATUS'
      ),
      value: inspectionStatusLabel(projection.inspection_status),
    },
    {
      label: t(
        'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.ACCEPTANCE_STATUS'
      ),
      value: acceptanceStatusLabel(projection.acceptance_status),
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.INSPECTED_AT'),
      value: formatDateTime(projection.inspected_at),
    },
    {
      label: t(
        'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.HAS_DIVERGENCE'
      ),
      value:
        projection.has_divergence === true
          ? t('SURVEYS.PREVIEW.YES')
          : t('SURVEYS.PREVIEW.NO'),
    },
  ];
};

const operationalProjectionAction = computed(() =>
  operationalProjectionDestination(
    operationalProjection.value,
    route?.params?.accountId
  )
);

const operationalProjectionActionLabel = computed(() => {
  if (operationalProjectionAction.value?.action === 'tasks') {
    return t(
      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.ACTION.OPEN_TASKS'
    );
  }

  return t(
    'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.ACTION.OPEN_DEMANDS'
  );
});

const openOperationalProjection = () => {
  if (!operationalProjectionAction.value) return;

  router.push(operationalProjectionAction.value.route);
  emit('close');
};

const operationalProjectionRows = computed(() => {
  const projection = operationalProjection.value;
  if (!projection) return [];

  return [
    {
      label: t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.FLOW'),
      value: operationalFlowLabels.value[projection.flow] || EMPTY_VALUE,
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.DESTINATION'),
      value: operationalProjectionTarget.value,
    },
    {
      label: t('SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.UPDATED_AT'),
      value: formatDateTime(projection.updated_at),
    },
    ...(isKycRequest.value
      ? operationalProjectionKycSummaryRows(projection, t)
      : inspectionProjectionRows(projection)),
  ].filter(row => row.value !== EMPTY_VALUE);
});
</script>

<template>
  <LaisDrawer
    :show="show"
    side="right"
    size="md"
    :title="t('SURVEYS.RESPONSES.DETAIL.TITLE')"
    :close-label="t('SURVEYS.RESPONSES.DETAIL.CLOSE')"
    @close="emit('close')"
  >
    <template #body>
      <div v-if="response" class="flex flex-col gap-5">
        <LaisStatusBadge
          :label="statusLabel(response.status)"
          :tone="statusTone(response.status)"
          dot
        />

        <dl class="grid gap-2">
          <div
            v-for="row in metaRows"
            :key="row.label"
            class="flex items-baseline justify-between gap-3 border-b border-lais-border-soft pb-2 last:border-0"
          >
            <dt
              class="shrink-0 text-lais-label-xs uppercase text-lais-text-muted"
            >
              {{ row.label }}
            </dt>
            <dd
              class="min-w-0 truncate text-right text-lais-body-sm font-medium text-lais-text"
            >
              {{ row.value }}
            </dd>
          </div>
        </dl>

        <section
          v-if="operationalProjection"
          class="overflow-hidden rounded-lais border border-lais-border bg-lais-bg-soft"
        >
          <div
            class="flex flex-col gap-3 border-b border-lais-border-soft bg-lais-bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <h3
              class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
            >
              {{ operationalProjectionTitle }}
            </h3>
            <LaisStatusBadge
              :label="
                operationalProjectionStatusLabel(operationalProjectionStatus, t)
              "
              :tone="operationalProjectionTone(operationalProjectionStatus)"
              dot
            />
          </div>

          <div class="flex flex-col gap-3 px-4 py-3">
            <dl class="grid gap-2">
              <div
                v-for="row in operationalProjectionRows"
                :key="row.label"
                class="flex items-baseline justify-between gap-3 border-b border-lais-border-soft pb-2 last:border-0"
              >
                <dt
                  class="shrink-0 text-lais-label-xs uppercase text-lais-text-muted"
                >
                  {{ row.label }}
                </dt>
                <dd
                  class="min-w-0 truncate text-right text-lais-body-sm font-medium text-lais-text"
                >
                  {{ row.value }}
                </dd>
              </div>
            </dl>
            <p class="text-lais-body-sm leading-relaxed text-lais-text-muted">
              {{
                isKycRequest
                  ? t(
                      'SURVEYS.RESPONSES.DETAIL.OPERATIONAL_PROJECTION.KYC.SAFE_SUMMARY'
                    )
                  : operationalProjectionStatusMessages[
                      operationalProjectionStatus
                    ] || operationalProjectionStatusMessages.failed
              }}
            </p>
            <LaisPrimaryButton
              v-if="operationalProjectionAction"
              size="sm"
              variant="ghost"
              icon="i-lucide-arrow-up-right"
              @click="openOperationalProjection"
            >
              {{ operationalProjectionActionLabel }}
            </LaisPrimaryButton>
          </div>
        </section>

        <section v-if="!isKycRequest" class="flex flex-col gap-3">
          <h3
            class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
          >
            {{ t('SURVEYS.RESPONSES.DETAIL.ANSWERS') }}
          </h3>

          <p
            v-if="!answerRows.length"
            class="rounded-lais border border-dashed border-lais-border px-3 py-6 text-center text-lais-body-sm text-lais-text-muted"
          >
            {{ t('SURVEYS.RESPONSES.DETAIL.NO_ANSWERS') }}
          </p>

          <article
            v-for="row in answerRows"
            :key="row.key"
            class="rounded-lais border border-lais-border-soft bg-lais-bg-card p-3"
          >
            <div class="flex items-start justify-between gap-2">
              <p class="min-w-0 text-[13px] font-semibold text-lais-text">
                {{ row.label }}
              </p>
              <span
                class="shrink-0 text-lais-label-xs uppercase text-lais-text-muted"
              >
                {{ questionTypeLabel(row.type) }}
              </span>
            </div>
            <p
              class="mt-1.5 whitespace-pre-line text-lais-body-sm leading-relaxed"
              :class="
                row.answered ? 'text-lais-text-body' : 'text-lais-text-muted'
              "
            >
              {{
                row.answered ? row.value : t('SURVEYS.RESPONSES.DETAIL.SKIPPED')
              }}
            </p>
          </article>
        </section>
      </div>
    </template>
  </LaisDrawer>
</template>
